import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { CHAPTERS } from "@/data/resources";

export default function Chapters() {
  const [active, setActive] = useState(0);
  const chapter = CHAPTERS[active];

  return (
    <section id="kapitler" data-testid="info-chapters-section" className="py-20 lg:py-28 bg-surface-subtle/60 border-y border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Veien videre</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Fire kapitler mot bedre økonomi
          </h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {CHAPTERS.map((c, i) => (
              <button
                key={c.id}
                data-testid={`chapter-tab-${c.id}`}
                onClick={() => setActive(i)}
                className={`flex items-center gap-4 text-left px-5 py-4 rounded-2xl border transition-all duration-300 min-w-[240px] lg:min-w-0 ${
                  i === active
                    ? "bg-surface border-primary/40 shadow-md shadow-primary/5"
                    : "border-transparent hover:bg-surface/70"
                }`}
              >
                <span className={`font-mono text-xs font-semibold ${i === active ? "text-terracotta" : "text-muted-foreground"}`}>
                  {c.id}
                </span>
                <span className={`font-serif font-bold text-lg leading-tight ${i === active ? "text-foreground" : "text-muted-foreground"}`}>
                  {c.title}
                </span>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={chapter.id}
                data-testid="chapter-content-card"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="bg-surface border border-border rounded-3xl p-8 lg:p-12"
              >
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">
                  Kapittel {chapter.id}
                </span>
                <h3 className="mt-3 font-serif font-bold text-2xl sm:text-3xl">{chapter.title}</h3>
                <p className="mt-4 text-muted-foreground leading-relaxed">{chapter.intro}</p>

                <ul className="mt-8 space-y-4">
                  {chapter.points.map((p) => (
                    <li key={p} className="flex gap-3 items-start">
                      <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 pt-8 border-t border-border flex flex-wrap gap-3">
                  {chapter.links.map((l) => (
                    <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                      data-testid={`chapter-link-${chapter.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold bg-accent text-accent-foreground px-4 py-2.5 rounded-full hover:bg-primary hover:text-primary-foreground transition-colors duration-200">
                      {l.label} <ArrowUpRight size={14} />
                    </a>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
